import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { JobApplication } from './ApplicationWizard'
import { MessageBox, useMessageBox } from './MessageBox'
import { LoadingSpinner } from './LoadingSpinner'
import { useSubscriptionContext } from '../contexts/SubscriptionContext'
import {
  StatusIcon,
  NotesIcon,
  IntelligentSearchIcon,
  ResumeAssignmentIcon,
  InterviewPrepIcon,
  GuidedPrepSummaryIcon,
  CommunicationCoachIcon,
  CompanyIntelIcon,
  EditIcon,
  DeleteIcon,
} from './ApplicationActionIcons'
import { PopupTooltip } from './PopupTooltip'
import './ApplicationsGrid.css'

const normalizeStatusValue = (value?: string | null): string =>
  String(value ?? '').trim().toLowerCase()

const formatStatusLabel = (value?: string | null): string => {
  const normalized = normalizeStatusValue(value)
  switch (normalized) {
    case 'pending':
      return 'Pending'
    case 'applied':
      return 'Applied'
    case 'interview':
    case 'interviewing':
      return 'Interviewing'
    case 'offer':
      return 'Offer'
    case 'rejected':
      return 'Rejected'
    case 'initialized':
      return 'Initialized'
    default: {
      const raw = String(value ?? '').trim()
      return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'Unknown'
    }
  }
}

const getGuidedPrepSummaryAvailability = (application: JobApplication) => {
  const summary =
    application.fullGuidedPrepSummary ||
    application.guidedPrepSummary ||
    null

  const available =
    Boolean(summary) ||
    Boolean(application.fullGuidedPrepSummaryAvailable ?? application.guidedPrepSummaryAvailable)

  return {
    available: Boolean(available),
    summary,
  }
}

interface ApplicationsGridProps {
  applications: JobApplication[]
  onEditApplication: (application: JobApplication) => void
  onViewApplication?: (application: JobApplication) => void
  onDeleteApplication: (id: string) => void
  onUpdateStatus: (id: string, status: JobApplication['status']) => void
  onOpenNotes: (application: JobApplication) => void
  onIntelligentSearch?: (application: JobApplication) => void
  onResumeAssignment?: (application: JobApplication) => void
  onCommunicationCoach?: (application: JobApplication) => void
  onCompanyIntelligence?: (application: JobApplication) => void
  onRefresh?: () => void
  isLoading?: boolean
  isListTruncated?: boolean
  displayLimit?: number
}

export const ApplicationsGrid: React.FC<ApplicationsGridProps> = ({
  applications,
  onEditApplication,
  onViewApplication,
  onDeleteApplication,
  onUpdateStatus,
  onOpenNotes,
  onIntelligentSearch,
  onResumeAssignment,
  onCommunicationCoach,
  onCompanyIntelligence,
  onRefresh,
  isLoading = false,
  isListTruncated = false,
  displayLimit = 200
}) => {
  const [isMounted, setIsMounted] = useState(false)
  
  useEffect(() => {
    setIsMounted(true)
  }, [])
  const { subscription } = useSubscriptionContext()
  const isExplorerPlan = subscription?.currentPlanId === "1"
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [companySearch, setCompanySearch] = useState<string>('')
  const [sortBy, setSortBy] = useState<string>('appliedDate')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [openStatusDropdown, setOpenStatusDropdown] = useState<string | null>(null)
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null)
  const [loaderPosition, setLoaderPosition] = useState<{ top: number; left: number; width: number; height: number } | null>(null)
  const badgeRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const gridRef = useRef<HTMLDivElement | null>(null)
  const itemsPerPage = 5
  const { messageBox, showMessageBox } = useMessageBox()

  const availableStatuses: JobApplication['status'][] = ['Pending', 'Applied', 'Interviewing', 'Offer', 'Rejected']

  // Calculate dropdown position when opened or on scroll/resize
  useEffect(() => {
    const updatePosition = () => {
      if (openStatusDropdown && badgeRefs.current[openStatusDropdown]) {
        const badge = badgeRefs.current[openStatusDropdown]
        if (badge) {
          const rect = badge.getBoundingClientRect()
          setDropdownPosition({
            top: rect.bottom + window.scrollY + 8,
            left: rect.left + window.scrollX
          })
        }
      }
    }

    if (openStatusDropdown) {
      updatePosition()
      // Update position on scroll or resize
      window.addEventListener('scroll', updatePosition, true)
      window.addEventListener('resize', updatePosition)
      return () => {
        window.removeEventListener('scroll', updatePosition, true)
        window.removeEventListener('resize', updatePosition)
      }
    } else {
      setDropdownPosition(null)
    }
  }, [openStatusDropdown])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.status-dropdown-container') && !target.closest('.status-dropdown-menu')) {
        setOpenStatusDropdown(null)
      }
    }
    
    if (openStatusDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openStatusDropdown])

  // Calculate loader position when loading or on scroll/resize
  useEffect(() => {
    const updateLoaderPosition = () => {
      if (isLoading && gridRef.current) {
        const rect = gridRef.current.getBoundingClientRect()
        setLoaderPosition({
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
          height: rect.height
        })
      } else {
        setLoaderPosition(null)
      }
    }

    if (isLoading) {
      updateLoaderPosition()
      // Update position on scroll or resize
      window.addEventListener('scroll', updateLoaderPosition, true)
      window.addEventListener('resize', updateLoaderPosition)
      return () => {
        window.removeEventListener('scroll', updateLoaderPosition, true)
        window.removeEventListener('resize', updateLoaderPosition)
      }
    } else {
      setLoaderPosition(null)
    }
  }, [isLoading])

  const getSortComparison = (aValue: string | number | Date, bValue: string | number | Date, order: 'asc' | 'desc'): number => {
    if (order === 'asc') {
      if (aValue < bValue) return -1
      if (aValue > bValue) return 1
      return 0
    } else {
      if (aValue > bValue) return -1
      if (aValue < bValue) return 1
      return 0
    }
  }

  const filteredApplications = useMemo(() => {
    const normalizedFilterStatus = normalizeStatusValue(filterStatus)
    const normalizedCompanySearch = companySearch.trim().toLowerCase()

    return applications.filter((app) => {
      const normalizedStatus = normalizeStatusValue(app.status)
      const companyName = String(app.company ?? '').toLowerCase()

      if (normalizedStatus === 'initialized') {
        return false
      }

      if (normalizedFilterStatus !== 'all' && normalizedStatus !== normalizedFilterStatus) {
        return false
      }

      if (normalizedCompanySearch && !companyName.includes(normalizedCompanySearch)) {
        return false
      }

      return true
    })
  }, [applications, companySearch, filterStatus])

  const sortedApplications = useMemo(() => {
    return [...filteredApplications].sort((a, b) => {
      // Validate that sortBy is a valid property of JobApplication
      const validSortFields: (keyof JobApplication)[] = [
        'appliedDate', 'postingDate', 'company', 'position', 'status', 'location'
      ];

      // Determine a single validated field to use throughout the comparator
      const sortField = validSortFields.includes(sortBy as keyof JobApplication)
        ? (sortBy as keyof JobApplication)
        : 'appliedDate';

      let aValue: any = a[sortField] || ''
      let bValue: any = b[sortField] || ''

      // Handle array fields by converting to string
      if (Array.isArray(aValue)) {
        aValue = aValue.join(', ')
      }
      if (Array.isArray(bValue)) {
        bValue = bValue.join(', ')
      }

      // Handle object fields by converting to string
      if (typeof aValue === 'object' && aValue !== null && !(aValue instanceof Date)) {
        aValue = JSON.stringify(aValue)
      }
      if (typeof bValue === 'object' && bValue !== null && !(bValue instanceof Date)) {
        bValue = JSON.stringify(bValue)
      }

      if (sortField === 'appliedDate' || sortField === 'postingDate') {
        aValue = new Date(aValue || 0).getTime()
        bValue = new Date(bValue || 0).getTime()
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      return getSortComparison(aValue, bValue, sortOrder)
    })
  }, [filteredApplications, sortBy, sortOrder])

  // Pagination calculations
  const totalPages = Math.ceil(sortedApplications.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedApplications = sortedApplications.slice(startIndex, endIndex)

  // Reset to page 1 when filter/sort changes
  React.useEffect(() => {
    setCurrentPage(1)
  }, [companySearch, filterStatus, sortBy, sortOrder])

  const getStatusIcon = (_status: string): React.ReactNode => {
    return <StatusIcon className="status-icon-svg" />
  }

  const getStatusTooltip = (status?: string | null): string =>
    `Current status: ${formatStatusLabel(status)}. Click to change it.`

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const handleDelete = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    
    // Find the application to get its details for the confirmation message
    const application = applications.find(app => app.id === id)
    const companyName = application?.company || 'this application'
    const positionName = application?.position || 'Unknown Position'
    
    showMessageBox({
      type: 'confirm',
      title: 'Delete Application',
      message: `Are you sure you want to delete the application for ${positionName} at ${companyName}?`,
      description: 'This action cannot be undone.',
      buttons: ['yes', 'no'],
      onButtonClick: (button) => {
        if (button === 'yes') {
          onDeleteApplication(id)
        }
      }
    })
  }, [onDeleteApplication, applications, showMessageBox])

  if (isLoading && applications.length === 0) {
    return (
      <div className="applications-empty applications-loading-state" role="status" aria-live="polite">
        <LoadingSpinner size="medium" />
        <p>Loading applications...</p>
      </div>
    )
  }

  if (applications.length === 0) {
    return (
      <div className="applications-empty">
        <div className="empty-icon">
          <span>📋</span>
        </div>
        <h3>No applications yet</h3>
        <p>Start tracking your job applications to see them here.</p>
      </div>
    )
  }

  return (
    <div className="applications-grid-container">
      <div className="applications-controls">
        <div className="applications-controls-primary">
          <div className="filter-controls">
            <label className="filter-label">Filter by status:</label>
            <select 
              value={filterStatus} 
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
              aria-label="Filter applications by status"
              data-tour-id="applications-filter"
            >
              <option value="all">All Applications</option>
              <option value="Pending">Pending</option>
              <option value="Applied">Applied</option>
              <option value="Interviewing">Interviewing</option>
              <option value="Offer">Offer</option>
              <option value="Rejected">Rejected</option>
            </select>
            {onRefresh && (
              <button
                className="refresh-button"
                onClick={onRefresh}
                disabled={isLoading}
                title="Refresh applications"
                aria-label="Refresh applications"
                data-tour-id="applications-refresh"
              >
                <span className="refresh-icon">🔄</span>
              </button>
            )}
          </div>

          <div className="sort-controls">
            <label className="sort-label">Sort by:</label>
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
              aria-label="Sort applications by field"
              data-tour-id="applications-sort"
            >
              <option value="appliedDate">Applied Date</option>
              <option value="company">Company</option>
              <option value="position">Position</option>
              <option value="status">Status</option>
              <option value="postingDate">Posting Date</option>
            </select>
            <button 
              className="sort-order-btn"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
              data-tour-id="applications-sort-order"
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>

          <div className="search-controls">
            <label className="search-label" htmlFor="applications-company-search">
              Company:
            </label>
            <input
              id="applications-company-search"
              type="search"
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              className="search-input"
              placeholder="Search by company"
              aria-label="Search applications by company"
            />
          </div>
        </div>

        <div className="applications-controls-secondary">
          <div className="applications-count-info" data-tour-id="applications-count">
            {totalPages > 1 ? (
              <span>Showing {startIndex + 1}-{Math.min(endIndex, sortedApplications.length)} of {sortedApplications.length} applications</span>
            ) : (
              <span>Showing {sortedApplications.length} of {applications.length} applications</span>
            )}
          </div>
          {isListTruncated && (
            <div className="applications-fetch-limit-note" role="status">
              Showing latest {displayLimit} applications in this view.
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-container">
              <div className="pagination-controls">
                <button 
                  className="pagination-btn"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  title="Previous page"
                >
                  ← Previous
                </button>
                <div className="pagination-numbers">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                    // Show first page, last page, current page, and pages around current
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      )
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} className="pagination-ellipsis">...</span>
                    }
                    return null
                  })}
                </div>
                <button 
                  className="pagination-btn"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  title="Next page"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="applications-grid" ref={gridRef}>
        {!isLoading && sortedApplications.length === 0 ? (
          <div className="applications-empty applications-empty--filtered">
            <div className="empty-icon">
              <span>🔎</span>
            </div>
            <h3>No applications match your filters</h3>
            <p>Try a different company name or clear the current status filter.</p>
          </div>
        ) : !isLoading && paginatedApplications.map((application, index) => {
          const normalizedStatus = normalizeStatusValue(application.status) || 'unknown'
          const statusLabel = formatStatusLabel(application.status)
          const guidedPrepSummary = getGuidedPrepSummaryAvailability(application)

          return (
            <div
              key={application.id || `application-${index}`}
              className="application-card-compact"
            >
            {/* Row 1: Company, Position, Status, Actions */}
            <div className="card-row-primary">
              <div className="company-position-group">
                <h3 className="company-name-compact">{application.company || 'Unknown Company'}</h3>
                <p className="position-title-compact">{application.position || 'No Position'}</p>
              </div>
              <div className="status-dropdown-container">
                <PopupTooltip content={getStatusTooltip(application.status)} placement="top">
                  <div 
                    ref={(el) => { if (application.id) { badgeRefs.current[application.id] = el } }}
                    className={`status-badge-compact status-${normalizedStatus} ${openStatusDropdown === application.id ? 'status-badge-active' : ''} cursor-pointer`}
                    onClick={(e) => {
                      e.stopPropagation()
                      setOpenStatusDropdown(openStatusDropdown === application.id ? null : (application.id || null))
                    }}
                    title="Click to change status"
                    data-tour-id="application-status"
                  >
                  <span className="status-icon-compact">{getStatusIcon(application.status || 'unknown')}</span>
                  <span className="status-text-compact">{statusLabel}</span>
                  <span className="status-dropdown-arrow">▼</span>
                </div>
                </PopupTooltip>
            </div>
              <PopupTooltip content="View notes for this application" placement="top">
              <div 
                className="notes-badge-compact cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenNotes(application)
                }}
                title="View notes"
                data-tour-id="application-notes"
              >
                <NotesIcon className="notes-icon-compact" />
                <span className="notes-count-compact">{application.notes_count ?? 0}</span>
              </div>
            </PopupTooltip>
            {guidedPrepSummary.available && (
              <PopupTooltip
                content="Full guided prep summary is available"
                placement="top"
              >
                <button
                  type="button"
                  className="guided-prep-summary-badge"
                  onClick={(e) => {
                    e.stopPropagation()
                    if (onViewApplication) {
                      onViewApplication(application)
                    }
                  }}
                  title="Full guided prep summary is available"
                  aria-label="Full guided prep summary is available"
                >
                  <GuidedPrepSummaryIcon className="guided-prep-summary-icon" />
                  <span className="guided-prep-summary-text">Guided Summary</span>
                </button>
              </PopupTooltip>
            )}
            <div className="card-actions-compact">
              {onIntelligentSearch && (
                <PopupTooltip content="Run intelligent search for related roles" placement="top">
                  <button 
                    className="intelligent-search-btn-compact"
                    onClick={(e) => {
                      e.stopPropagation()
                      onIntelligentSearch(application)
                    }}
                    title="Intelligent Search"
                    data-tour-id="application-intelligent-search"
                  >
                    <IntelligentSearchIcon className="action-icon" />
                  </button>
                </PopupTooltip>
              )}
              {onResumeAssignment && application.id && (
                <PopupTooltip content="Choose which resume to use for this application" placement="top">
                  <button 
                    className="resume-assignment-btn-compact"
                    onClick={(e) => {
                      e.stopPropagation()
                      onResumeAssignment(application)
                    }}
                    title="Resume Assignment"
                    data-tour-id="application-resume-assignment"
                  >
                    <ResumeAssignmentIcon className="action-icon" />
                  </button>
                </PopupTooltip>
              )}
              {onViewApplication && (
                <PopupTooltip content="Open interview preparation for this job" placement="top">
                  <button 
                    className="view-btn-compact interview-prep-btn"
                    onClick={() => onViewApplication(application)}
                    title="Interview Preparation"
                    data-tour-id="application-interview-prep"
                  >
                    <InterviewPrepIcon className="action-icon" />
                  </button>
                </PopupTooltip>
              )}
              {onCommunicationCoach && (
                <PopupTooltip content="Practice communication and response coaching" placement="top">
                  <button 
                    className="view-btn-compact communication-coach-btn"
                    onClick={() => onCommunicationCoach(application)}
                    title="Communication Coach"
                    data-tour-id="application-communication-coach"
                  >
                    <CommunicationCoachIcon className="action-icon" />
                  </button>
                </PopupTooltip>
              )}
              {onCompanyIntelligence && application.company && (
                <PopupTooltip content="Open company intelligence and research" placement="top">
                  <button 
                    className="view-btn-compact company-intelligence-btn"
                    onClick={(e) => {
                      e.stopPropagation()
                      onCompanyIntelligence(application)
                    }}
                    title="Company Intelligence"
                    data-tour-id="application-company-intel"
                  >
                    <CompanyIntelIcon className="action-icon" />
                  </button>
                </PopupTooltip>
              )}
              <PopupTooltip content="Edit this application" placement="top">
                <button 
                  className="edit-btn-compact"
                  onClick={() => onEditApplication(application)}
                  title="Edit application"
                  data-tour-id="application-edit"
                >
                  <EditIcon className="action-icon" />
                </button>
              </PopupTooltip>
              <PopupTooltip
                content={isExplorerPlan ? "Delete not available on Explorer Plan" : "Delete this application"}
                placement="top"
              >
                <span className="application-action-tooltip-trigger">
                  <button 
                    className="delete-btn-compact"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete(application.id!, e)
                    }}
                    title={isExplorerPlan ? "Delete not available on Explorer Plan" : "Delete application"}
                    disabled={isExplorerPlan}
                    style={{ opacity: isExplorerPlan ? 0.5 : 1, cursor: isExplorerPlan ? 'not-allowed' : 'pointer' }}
                    data-tour-id="application-delete"
                  >
                    <DeleteIcon className="action-icon" />
                  </button>
                </span>
              </PopupTooltip>
            </div>
            </div>

            {/* Row 2: Location, Salary, Work Type, Technologies, Dates, URL */}
            <div className="card-row-secondary">
              <div className="details-row">
                {application.location && (
                  <div className="detail-item-compact">
                    <span className="detail-icon">📍</span>
                    <span className="detail-text">{application.location}</span>
                  </div>
                )}
                {application.salary && (
                  <div className="detail-item-compact">
                    <span className="detail-icon">💰</span>
                    <span className="detail-text">{application.salary}</span>
                  </div>
                )}
                {application.workType && (
                  <div className="detail-item-compact">
                    <span className="detail-icon">🏢</span>
                    <span className="detail-text">{application.workType}</span>
                  </div>
                )}
                {application.technologies && application.technologies.length > 0 && (
                  <div className="detail-item-compact">
                    <span className="detail-icon">⚙️</span>
                    <span className="detail-text">
                      {application.technologies.slice(0, 5).join(', ')}
                      {application.technologies.length > 5 && ` +${application.technologies.length - 5}`}
                    </span>
                  </div>
                )}
              </div>
              <div className="meta-row">
                <div className="dates-compact">
                  <span className="date-compact">Applied: {formatDate(application.appliedDate)}</span>
                  {application.postingDate && (
                    <span className="date-compact">Posted: {formatDate(application.postingDate)}</span>
                  )}
                </div>
                {application.url && (
                  <a 
                    href={application.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="original-url-compact"
                    onClick={(e) => e.stopPropagation()}
                    title="Open original job posting"
                    aria-label="Open original job posting"
                    data-tour-id="application-view-original"
                  >
                    View Original
                  </a>
                )}
              </div>
            </div>
          </div>
          )
        })}
      </div>

      {/* Status Dropdown Portal */}
      {openStatusDropdown && dropdownPosition && isMounted && typeof document !== 'undefined' && document.body && (() => {
        const application = applications.find(app => app.id === openStatusDropdown)
        if (!application) return null
        
        return createPortal(
          <div 
            className="status-dropdown-menu status-dropdown-menu-portal"
            style={{
              position: 'fixed',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              zIndex: 10000
            }}
          >
            {availableStatuses.map((status) => {
              const isActiveStatus =
                normalizeStatusValue(status) === normalizeStatusValue(application.status)

              return (
                <button
                  key={status}
                  className={`status-dropdown-item status-${status.toLowerCase()} ${isActiveStatus ? 'status-dropdown-item-active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (!isActiveStatus && application.id) {
                      onUpdateStatus(application.id, status)
                    }
                    setOpenStatusDropdown(null)
                  }}
                  title={`Set status to ${status}`}
                  aria-label={`Set status to ${status}`}
                >
                  <span className="status-dropdown-icon">{getStatusIcon(status)}</span>
                  <span className="status-dropdown-text">{status}</span>
                  {isActiveStatus && (
                    <span className="status-dropdown-check">✓</span>
                  )}
                </button>
              )
            })}
          </div>,
          document.body
        )
      })()}

      {/* Loading Overlay Portal */}
      {isLoading && loaderPosition && isMounted && typeof document !== 'undefined' && document.body && createPortal(
        <div 
          className="card-loading-overlay card-loading-overlay-portal"
          style={{
            position: 'fixed',
            top: `${loaderPosition.top}px`,
            left: `${loaderPosition.left}px`,
            width: `${loaderPosition.width}px`,
            height: `${loaderPosition.height}px`,
            zIndex: 9999
          }}
        >
          <LoadingSpinner size="medium" />
          <p>Loading applications...</p>
        </div>,
        document.body
      )}

      {/* Delete Confirmation MessageBox */}
      <MessageBox {...messageBox} />
    </div>
  )
}
